import type { z } from 'zod';

import * as React from 'react';

/**
 * Hook to persist a stateful value in local storage validated by a Zod schema.
 */
export function useLocalState<T extends z.ZodTypeAny>(options: {
  /** The key to use for the local storage item. */
  key: string;
  /**
   * The default value to use if the local storage item is not found. It must
   * match the value as parsed by the schema.
   */
  defaultValue: z.infer<T>;
  /** The schema to use for parsing the local storage item. */
  schema: T;
}): ReturnType<typeof React.useState<z.infer<T>>> {
  const [value, setValue] = React.useState(() => {
    try {
      const item = localStorage.getItem(options.key);
      if (item === null) return options.defaultValue;
      const parsed: unknown = JSON.parse(item);
      // Inferring the type as suggested in the docs:
      // https://v3.zod.dev/?id=inferring-the-inferred-type
      return options.schema.parse(parsed) as z.infer<T>;
    } catch {
      return options.defaultValue;
    }
  });

  React.useEffect(() => {
    try {
      localStorage.setItem(options.key, JSON.stringify(value));
    } catch {
      // Ignore for cases where `localStorage` might not available to prevent
      // spamming the console.
    }
  }, [options.key, value]);

  return [value, setValue] as const;
}
